<template>
  <div class="ss-wrapper" :class="{ open: isOpen, disabled }" ref="root">
    <div
      :class="[
        'ss-control',
        useCompanyFilter ? 'company-filter-select' : null,
        multiple ? 'ss-control--multiple' : null,
        controlClass,
      ]"
      :id="id"
      role="combobox"
      :aria-expanded="String(isOpen)"
      :aria-owns="dropdownId"
      :aria-controls="dropdownId"
      tabindex="0"
      :title="selectedTooltip"
      @click="toggleOpen"
      @keydown.down.prevent="openAndFocus()"
      @keydown.enter.prevent="toggleOpen"
      @keydown.space.prevent="toggleOpen"
    >
      <template v-if="multiple">
        <div v-if="selectedOptions.length" class="ss-tags">
          <span
            v-for="option in selectedOptions"
            :key="getTagKey(option)"
            class="ss-tag"
          >
            <span class="ss-tag__label">{{ option.selectedText }}</span>
            <button
              type="button"
              class="ss-tag__remove"
              aria-label="Remover item selecionado"
              @mousedown.stop.prevent="removeValue(option.value)"
              @click.stop.prevent
            >
              <i class="fa fa-times" aria-hidden="true"></i>
            </button>
          </span>
        </div>
        <span v-else class="ss-placeholder">{{ placeholder }}</span>
      </template>
      <span v-else-if="selectedText" class="ss-value">{{ selectedText }}</span>
      <span v-else class="ss-placeholder">{{ placeholder }}</span>
      <i class="fa fa-chevron-down ss-caret" aria-hidden="true"></i>
    </div>

    <transition name="ss-fade">
      <div
        v-if="isOpen"
        :id="dropdownId"
        class="ss-dropdown"
        role="listbox"
        :aria-multiselectable="multiple ? 'true' : 'false'"
      >
        <div class="ss-search">
          <input
            ref="search"
            v-model="query"
            type="text"
            class="ss-search-input"
            :placeholder="searchPlaceholder"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="selectActive()"
            @keydown.esc.prevent="close()"
          />
        </div>
        <ul class="ss-options">
          <li
            v-for="(opt, idx) in filteredOptions"
            :key="getKey(opt, idx)"
            class="ss-option"
            :class="{
              active: idx === activeIndex,
              selected: isSelected(opt.value),
            }"
            role="option"
            :aria-selected="String(isSelected(opt.value))"
            @mouseenter="activeIndex = idx"
            @mousedown.prevent="select(opt)"
          >
            <span v-if="multiple" class="ss-option-check" aria-hidden="true">
              <i
                class="fa"
                :class="isSelected(opt.value) ? 'fa-check-square' : 'fa-square'"
              ></i>
            </span>
            {{ opt.text }}
          </li>
          <li v-if="filteredOptions.length === 0" class="ss-empty">
            Sem resultados
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "SearchableSelect",
  model: { prop: "value", event: "input" },
  props: {
    value: {
      type: [String, Number, Boolean, Object, Array, null],
      default: null,
    },
    options: {
      type: Array,
      default: () => [], // array of { value, text }
    },
    placeholder: { type: String, default: "Selecione" },
    searchPlaceholder: { type: String, default: "Buscar…" },
    id: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    // Extra CSS class(es) applied to the control div (e.g. 'form-control')
    controlClass: { type: [String, Array, Object], default: null },
    // When true, applies the 'company-filter-select' look (for the dashboard)
    useCompanyFilter: { type: Boolean, default: true },
  },
  data() {
    return {
      isOpen: false,
      query: "",
      activeIndex: 0,
      internalValue: this.normalizeValue(this.value),
      dropdownId: `ss-${Math.random().toString(36).slice(2)}`,
    };
  },
  computed: {
    normalizedOptions() {
      return this.options.map((o) => ({
        value: Object.prototype.hasOwnProperty.call(o, "value") ? o.value : o,
        text: Object.prototype.hasOwnProperty.call(o, "text")
          ? o.text
          : String(o),
        selectedText: Object.prototype.hasOwnProperty.call(o, "selectedText")
          ? o.selectedText
          : Object.prototype.hasOwnProperty.call(o, "text")
          ? o.text
          : String(o),
      }));
    },
    filteredOptions() {
      const q = (this.query || "").toString().toLowerCase().trim();
      if (!q) return this.normalizedOptions;
      return this.normalizedOptions.filter((o) =>
        (o.text || "").toLowerCase().includes(q)
      );
    },
    selectedOptions() {
      if (!this.multiple) {
        return [];
      }

      return this.normalizedOptions.filter((option) =>
        this.isSelected(option.value)
      );
    },
    selectedText() {
      if (this.multiple) {
        const labels = this.selectedOptions.map(
          (option) => option.selectedText
        );

        if (!labels.length) {
          return "";
        }

        if (labels.length <= 2) {
          return labels.join(", ");
        }

        return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
      }

      const match = this.normalizedOptions.find((o) =>
        this.equals(o.value, this.internalValue)
      );
      return match ? match.text : "";
    },
    selectedTooltip() {
      if (this.multiple) {
        return this.selectedOptions
          .map((option) => option.selectedText)
          .join(", ");
      }

      return this.selectedText;
    },
  },
  watch: {
    value(val) {
      this.internalValue = this.normalizeValue(val);
    },
    options() {
      // Keep active index in bounds when options change
      if (this.activeIndex >= this.filteredOptions.length) this.activeIndex = 0;
    },
    isOpen(open) {
      if (open) {
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
        document.addEventListener("click", this.onClickOutside);
      } else {
        document.removeEventListener("click", this.onClickOutside);
        this.query = "";
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onClickOutside);
  },
  methods: {
    normalizeValue(value) {
      if (!this.multiple) {
        return value;
      }

      if (Array.isArray(value)) {
        return [...value];
      }

      if (value === null || value === undefined || value === "") {
        return [];
      }

      return [value];
    },
    equals(a, b) {
      // Strict equality with null/undefined handling and primitive support
      return a === b || (a == null && b == null);
    },
    isSelected(value) {
      if (!this.multiple) {
        return this.equals(value, this.internalValue);
      }

      return this.internalValue.some((selectedValue) =>
        this.equals(selectedValue, value)
      );
    },
    getKey(opt, idx) {
      return opt.value != null ? `opt-${String(opt.value)}` : `idx-${idx}`;
    },
    getTagKey(option) {
      return option.value != null
        ? `tag-${String(option.value)}`
        : `tag-${option.selectedText}`;
    },
    toggleOpen() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
    },
    openAndFocus() {
      if (this.disabled) return;
      if (!this.isOpen) this.isOpen = true;
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
    },
    close() {
      this.isOpen = false;
    },
    onClickOutside(e) {
      if (!this.$refs.root) return;
      if (!this.$refs.root.contains(e.target)) {
        this.close();
      }
    },
    move(dir) {
      const len = this.filteredOptions.length;
      if (!len) return;
      let i = this.activeIndex + dir;
      if (i < 0) i = len - 1;
      if (i >= len) i = 0;
      this.activeIndex = i;
      this.$nextTick(() => this.scrollActiveIntoView());
    },
    scrollActiveIntoView() {
      const list = this.$el.querySelector(".ss-options");
      const active = this.$el.querySelector(".ss-option.active");
      if (!list || !active) return;
      const listRect = list.getBoundingClientRect();
      const itemRect = active.getBoundingClientRect();
      if (itemRect.top < listRect.top)
        list.scrollTop -= listRect.top - itemRect.top;
      if (itemRect.bottom > listRect.bottom)
        list.scrollTop += itemRect.bottom - listRect.bottom;
    },
    select(opt) {
      if (this.multiple) {
        const nextValue = [...this.internalValue];
        const selectedIndex = nextValue.findIndex((selectedValue) =>
          this.equals(selectedValue, opt.value)
        );

        if (selectedIndex >= 0) {
          nextValue.splice(selectedIndex, 1);
        } else {
          nextValue.push(opt.value);
        }

        this.internalValue = nextValue;
        this.$emit("input", [...this.internalValue]);
        this.$emit("change", [...this.internalValue]);
        return;
      }

      this.internalValue = opt.value;
      this.$emit("input", this.internalValue);
      this.$emit("change", this.internalValue);
      this.close();
    },
    removeValue(value) {
      if (!this.multiple || this.disabled) {
        return;
      }

      this.internalValue = this.internalValue.filter(
        (selectedValue) => !this.equals(selectedValue, value)
      );
      this.$emit("input", [...this.internalValue]);
      this.$emit("change", [...this.internalValue]);
    },
    selectActive() {
      const opt = this.filteredOptions[this.activeIndex];
      if (opt) this.select(opt);
    },
  },
};
</script>

<style scoped>
/* Wrapper aligns with parent width */
.ss-wrapper {
  position: relative;
  width: 100%;
}
.ss-wrapper.disabled .ss-control {
  background: #f9fafb;
  color: #a0aec0;
  cursor: not-allowed;
}

/* Control mimics current .company-filter-select look (border, radius, padding) */
.ss-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  background: white;
  width: 100%;
}
.ss-control--multiple {
  min-height: calc(1.5em + 0.75rem + 2px);
  align-items: flex-start;
}
.ss-control:focus {
  outline: none;
}
.ss-wrapper.open .company-filter-select {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}
.ss-placeholder {
  color: #4a5568;
  font-size: 0.9rem;
}
.ss-value {
  color: #4a5568;
  font-size: 0.9rem;
}
.ss-tags {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 0.375rem;
  min-width: 0;
}
.ss-tag {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: #e7f1ff;
  color: #1d4f91;
  font-size: 0.8rem;
  line-height: 1.2;
}
.ss-tag__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ss-tag__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.35rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.ss-tag__remove:focus {
  outline: none;
}
.ss-caret {
  color: #4a5568;
  font-size: 0.8rem;
  margin-left: 0.5rem;
  margin-top: 0.35rem;
  flex-shrink: 0;
}

/* Dropdown panel */
.ss-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  z-index: 1000;
}

.ss-search {
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}
.ss-search-input {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.375rem 0.5rem;
  font-size: 0.9rem;
  color: #4a5568;
}
.ss-search-input:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.ss-options {
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  max-height: 220px;
  overflow: auto;
}
.ss-option {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  color: #1a202c;
}
.ss-option-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  margin-right: 0.5rem;
  color: #4299e1;
}
.ss-option.active {
  background: #f7fafc;
}
.ss-option.selected {
  font-weight: 600;
}
.ss-empty {
  padding: 0.75rem;
  color: #718096;
  font-size: 0.9rem;
}

/* Simple fade for dropdown */
.ss-fade-enter-active,
.ss-fade-leave-active {
  transition: opacity 0.12s ease;
}
.ss-fade-enter,
.ss-fade-leave-to {
  opacity: 0;
}
</style>
